import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { equipment as PrismaEquipment, store as PrismaStore } from '@prisma/client';
import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { RecurrenceEditModeDialog, type RecurrenceEditMode } from '~/components/ui/RecurrenceEditModeDialog';
import LocalDateTime, { IANATimezone } from '~/lib/localdatetime';
import { useUserStore } from '~/stores/user-store';
import { EquipmentScheduleEntryType } from '~/types/server-types/EquipmentScheduleEntryType';
import { StoreScheduleEntryBlockedType } from '~/types/server-types/StoreScheduleEntryBlockedType';
import { StoreScheduleEntryType } from '~/types/server-types/StoreScheduleEntryType';
import { UserRole } from '~/types/server-types/UserRole';

const formatDate = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('yyyy-MM-dd') : '';
};

const formatTime = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('HH:mm') : '';
};

const parseDate = (dateStr: string, existingTime: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!dateStr) return null;
    const time = existingTime ? existingTime.format('HH:mm') : '00:00';
    return new LocalDateTime(`${dateStr} ${time}`, timezone);
};

const parseTime = (timeStr: string, existingDate: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!timeStr || !existingDate) return null;
    const date = existingDate.format('yyyy-MM-dd');
    return new LocalDateTime(`${date} ${timeStr}`, timezone);
};

type Store = Pick<PrismaStore, 'id' | 'name'>;
type Equipment = Pick<PrismaEquipment, 'id' | 'name'>

interface ScheduleEntryFormProps {
    storeId: number;
    userTimezone: string; // Pass user's timezone
    scheduleEntryId?: string; // For modifying an existing schedule entry, this is the ID of the entry.
    overwriteEntryType?: StoreScheduleEntryType | EquipmentScheduleEntryType;
    selectedSlot?: { start: LocalDateTime, end: LocalDateTime }; // Optional prefill from calendar click
    onSuccess: () => void; // To close dialog and refresh
    onCancel: () => void; // To close dialog
}

export const StoreScheduleEntryForm: React.FC<ScheduleEntryFormProps> = ({
    scheduleEntryId,
    storeId,
    userTimezone,
    overwriteEntryType,
    selectedSlot,
    onSuccess,
    onCancel
}) => {
    console.log("selectedSlot");
    console.log(selectedSlot);
    const isEditing = !!scheduleEntryId;
    const userRole = useUserStore(state => state.user?.role);
    const canManageShiftEntries = userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;
    const utils = trpc.useUtils();

    const [onlyUpdateForOriginalStore, setOnlyUpdateForOriginalStore] = useState(false);

    const [originalSeriesStart, setOriginalSeriesStart] = useState<LocalDateTime | null>(null);
    const [originalSeriesEnd, setOriginalSeriesEnd] = useState<LocalDateTime | null>(null);

    const originalStoreData = trpc.stores.getOne.useQuery({
        id: storeId,
    });

    const storeScheduleEntryQuery = trpc.stores.getScheduleEntry.useQuery({
        id: scheduleEntryId || ""
    }, {
        enabled: !!scheduleEntryId
    })

    const existingScheduleEntryData: typeof storeScheduleEntryQuery.data | null = useMemo(() => {
        if (!isEditing) {
            return null;
        }
        return storeScheduleEntryQuery.data ?? null;
    }, [isEditing, storeScheduleEntryQuery]);

    const [type, setType] = useState<StoreScheduleEntryType>(StoreScheduleEntryType.AVAILABLE);
    const [blockedType, setBlockedType] = useState<StoreScheduleEntryBlockedType | null>(null);
    const [description, setDescription] = useState<string>('');

    const [startTimeObj, setStartTimeObj] = useState<LocalDateTime | null>(() => {
        // if (initialData?.startTime) return dayjs(initialData.startTime).tz(userTimezone);
        if (selectedSlot?.start) return selectedSlot.start;
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(1, 'hour').utc().toDate(), 'Europe/Berlin');
    });
    const [endTimeObj, setEndTimeObj] = useState<LocalDateTime | null>(() => {
        // if (initialData?.endTime) return dayjs(initialData.endTime).tz(userTimezone);
        if (selectedSlot?.end) return selectedSlot.end;
        if (startTimeObj) return startTimeObj.clone().addMinutes(60);
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(2, 'hour').utc().toDate(), 'Europe/Berlin');
    });

    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceEndDateObj, setRecurrenceEndDateObj] = useState<dayjs.Dayjs | null>(dayjs().endOf('year'));

    const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY'); // Default
    const [interval, setInterval] = useState<number>(1); // Default: Every 1 day/week/month
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>({ // For weekly selection
        MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false,
    });

    useEffect(() => {
        if (!isEditing || !existingScheduleEntryData) {
            return;
        }
        // Initialize data from existing schedule entry
        setType(existingScheduleEntryData.type);
        if ('blockedType' in existingScheduleEntryData) {
            setBlockedType(existingScheduleEntryData.blockedType);
        }
        setDescription(existingScheduleEntryData.description || '');

        const seriesStart = LocalDateTime.deserialize(existingScheduleEntryData.startTimeLocal);
        const seriesEnd = LocalDateTime.deserialize(existingScheduleEntryData.endTimeLocal);
        setOriginalSeriesStart(seriesStart);
        setOriginalSeriesEnd(seriesEnd);

        if (existingScheduleEntryData.isRecurring && selectedSlot) {
            setStartTimeObj(selectedSlot.start);
            setEndTimeObj(selectedSlot.end);
        } else {
            setStartTimeObj(seriesStart);
            setEndTimeObj(seriesEnd);
        }

        if (existingScheduleEntryData.recurrenceEndDate) {
            setRecurrenceEndDateObj(dayjs(existingScheduleEntryData.recurrenceEndDate).tz(userTimezone));
        } else if (existingScheduleEntryData.isRecurring) {
            setRecurrenceEndDateObj(null);
        }
    }, [existingScheduleEntryData]);

    // *** We will now DERIVE the recurrenceRule using useEffect ***
    const [derivedRecurrenceRule, setDerivedRecurrenceRule] = useState<string>('');

    // Update end time if start time changes and it was the default duration
    useEffect(() => {
        if (startTimeObj && !existingScheduleEntryData?.endTime && !selectedSlot?.end) {
            setEndTimeObj(startTimeObj.clone().addMinutes(60));
        }
    }, [startTimeObj, existingScheduleEntryData?.endTime, selectedSlot?.end]);


    useEffect(() => {
        if (existingScheduleEntryData?.isRecurring && existingScheduleEntryData.recurrenceRule) {
            try {
                // Basic parsing for FREQ, INTERVAL, BYDAY (add more if needed)
                const ruleParts = existingScheduleEntryData.recurrenceRule.split(';');
                let initialFreq: 'DAILY' | 'WEEKLY' | 'MONTHLY' = 'WEEKLY'; // Default fallback
                let initialInterval = 1;
                const initialWeekdays: Record<string, boolean> = { MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false };

                ruleParts.forEach(part => {
                    const [key, value] = part.split('=');
                    if (key === 'FREQ' && (value === 'DAILY' || value === 'WEEKLY' || value === 'MONTHLY')) {
                        initialFreq = value;
                    } else if (key === 'INTERVAL') {
                        initialInterval = parseInt(value, 10) || 1;
                    } else if (key === 'BYDAY' && value) {
                        value.split(',').forEach(day => {
                            if (day in initialWeekdays) {
                                initialWeekdays[day] = true;
                            }
                        });
                    }
                });

                setFrequency(initialFreq);
                setInterval(initialInterval);
                setWeekdays(initialWeekdays);
                // Also set the main toggle
                setIsRecurring(true);

            } catch (e) {
                console.error("Could not parse initial RRULE:", existingScheduleEntryData.recurrenceRule, e);
                // Fallback: Keep isRecurring false or show raw input? For now, we assume simple rules.
                setIsRecurring(false); // Safer fallback if parsing fails
            }
        } else if (!existingScheduleEntryData?.id) {
            // Reset builder state if creating a new entry (or if existingScheduleEntryData is cleared)
            // Set default based on start date when isRecurring is first checked (see next useEffect)
            setIsRecurring(existingScheduleEntryData?.isRecurring ?? false); // Respect existingScheduleEntryData if provided but no rule
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingScheduleEntryData]); // Run only when existingScheduleEntryData changes

    const dayjsToRruleWeekdays: { [key: number]: string } = {
        0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA'
    };

    useEffect(() => {
        if (!isRecurring) {
            setDerivedRecurrenceRule(''); // Clear rule if not recurring
            // Optionally reset builder state here too for cleanliness
            // setFrequency('WEEKLY');
            // setInterval(1);
            // setWeekdays({ MO: false, ... });
            return;
        }

        let parts: string[] = [];
        parts.push(`FREQ=${frequency}`);

        if (interval > 1) {
            parts.push(`INTERVAL=${interval}`);
        }

        if (frequency === 'WEEKLY') {
            const selectedDays = Object.entries(weekdays)
                .filter(([, isSelected]) => isSelected)
                .map(([day]) => day);

            if (selectedDays.length > 0) {
                parts.push(`BYDAY=${selectedDays.join(',')}`);
            } else {
                // Handle case where no days are selected - maybe default to start day?
                // Or rely on validation. For now, generate potentially invalid rule & validate later.
            }
        }

        // For MONTHLY, we implicitly rely on the DTSTART day by default (no BYMONTHDAY/BYDAY needed for simple case)

        setDerivedRecurrenceRule(parts.join(';'));

    }, [isRecurring, frequency, interval, weekdays]);


    // Effect to set default weekday based on start time when switching to weekly/recurring
    useEffect(() => {
        if (isRecurring && frequency === 'WEEKLY' && startTimeObj) {
            // Only set default if *no* days are currently selected (avoid overriding user choice)
            const anyDaySelected = Object.values(weekdays).some(selected => selected);
            if (!anyDaySelected) {
                const startDay = dayjsToRruleWeekdays[startTimeObj.getDayOfWeek()];
                if (startDay) {
                    setWeekdays(prev => ({ ...prev, [startDay]: true }));
                }
            }
        }
        // If switching away from weekly, clear weekdays? Optional, maybe keep selection.
        // else if (!isRecurring || frequency !== 'WEEKLY') {
        //     setWeekdays({ MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false });
        // }

    }, [isRecurring, frequency, startTimeObj]); // Rerun if start time changes while weekly is active


    // --- Mutations ---
    const createStoreScheduleEntryMutation = trpc.stores.createScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry created." });
            utils.stores.getScheduleEntries.invalidate({ storeId: storeId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess(); // Close dialog etc.
        },
        onError: (err) => {
            console.error('Failed to create schedule entry:', err);
            toast.error("Error", { description: `Failed to create entry: ${err.message}` });
        },
    });

    const updateStoreScheduleEntryMutation = trpc.stores.updateScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry updated." });
            utils.stores.getScheduleEntries.invalidate({ storeId: storeId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess(); // Close dialog etc.
        },
        onError: (err) => {
            console.error('Failed to update schedule entry:', err);
            toast.error("Error", { description: `Failed to update entry: ${err.message}` });
        },
    });

    const deleteStoreScheduleEntryMutation = trpc.stores.deleteScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry deleted." });
            utils.stores.getScheduleEntries.invalidate({ storeId: storeId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess();
        },
        onError: (err) => {
            console.error('Failed to delete schedule entry:', err);
            toast.error("Error", { description: `Failed to delete entry: ${err.message}` });
        },
    });

    useEffect(() => {
        // Set selectedDataObjects to the original store
        if (onlyUpdateForOriginalStore === true) {
            if (originalStoreData.data) {
                setSelectedDataObject(originalStoreData.data);
            }
        } else {
            // Back to default
            if (existingScheduleEntryData) {
                setSelectedDataObject(existingScheduleEntryData.store);
            }
        }
    }, [onlyUpdateForOriginalStore]);

    const [selectedDataObject, setSelectedDataObject] = useState<Store | undefined>(undefined);

    const [recurrenceEditDialogOpen, setRecurrenceEditDialogOpen] = useState(false);
    const [recurrenceEditDialogIsDelete, setRecurrenceEditDialogIsDelete] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<Record<string, any> | null>(null);

    useEffect(() => {
        if (!selectedDataObject && isEditing === false && !!storeId && originalStoreData.data) {
            setSelectedDataObject(originalStoreData.data);
        }
    }, [originalStoreData, isEditing, storeId]);

    useEffect(() => {
        if (existingScheduleEntryData) {
            setSelectedDataObject(existingScheduleEntryData.store);
        }
    }, [existingScheduleEntryData]);


    const mutationLoading = createStoreScheduleEntryMutation.isPending || updateStoreScheduleEntryMutation.isPending;
    const deleteMutationLoading = deleteStoreScheduleEntryMutation.isPending;

    // --- Submission ---
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canManageShiftEntries) return;

        if (!startTimeObj || !endTimeObj) {
            toast.error("Error", { description: "Please provide valid start and end times." });
            return;
        }

        if (endTimeObj.lessThanOrEqual(startTimeObj)) {
            toast.error("Error", { description: "End time must be after start time." });
            return;
        }

        if (isRecurring && !derivedRecurrenceRule) {
            toast.error("Error", { description: "Recurrence rule (RRULE) is required for recurring entries." });
            return;
        }

        if (isEditing && isRecurring && scheduleEntryId) {
            setPendingFormData({ action: 'update' });
            setRecurrenceEditDialogIsDelete(false);
            setRecurrenceEditDialogOpen(true);
            return;
        }

        executeSubmit('all');
    };

    const executeSubmit = (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj) return;

        const selectedSlotDay = selectedSlot ? selectedSlot.start.format("yyyy-MM-dd") : null;

        let submitStartTime = startTimeObj;
        let submitEndTime = endTimeObj;

        if (editMode === 'all' && originalSeriesStart && originalSeriesEnd) {
            submitStartTime = originalSeriesStart.clone().setHours(startTimeObj.getHours()).setMinutes(startTimeObj.getMinutes());
            submitEndTime = originalSeriesEnd.clone().setHours(endTimeObj.getHours()).setMinutes(endTimeObj.getMinutes());
        }

        const mutationData = {
            storeId,
            type,
            blockedType,
            description: description || undefined,
            startTimeLocalSerialized: submitStartTime.serialize(),
            endTimeLocalSerialized: submitEndTime.serialize(),
            isRecurring,
            recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
            recurrenceEndDate: isRecurring ? (recurrenceEndDateObj ? recurrenceEndDateObj.endOf('day').toISOString() : undefined) : undefined,
        };

        if (isEditing && scheduleEntryId) {
            updateStoreScheduleEntryMutation.mutate({
                id: scheduleEntryId,
                editMode,
                selectedSlotDay,
                ...mutationData,
            });
        } else {
            createStoreScheduleEntryMutation.mutate(mutationData);
        }
    };

    const handleDelete = () => {
        if (!canManageShiftEntries) return;
        if (!scheduleEntryId) {
            return;
        }
        if (isRecurring) {
            setPendingFormData({ action: 'delete' });
            setRecurrenceEditDialogIsDelete(true);
            setRecurrenceEditDialogOpen(true);
            return;
        }
        executeDelete('all');
    };

    const executeDelete = (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!scheduleEntryId) return;

        const selectedSlotDay = selectedSlot ? selectedSlot.start.format("yyyy-MM-dd") : null;

        deleteStoreScheduleEntryMutation.mutate({
            id: scheduleEntryId,
            editMode,
            selectedSlotDay,
        });
    };

    const handleRecurrenceEditModeSelect = (mode: RecurrenceEditMode) => {
        setRecurrenceEditDialogOpen(false);
        if (pendingFormData?.action === 'delete') {
            executeDelete(mode);
        } else {
            executeSubmit(mode);
        }
        setPendingFormData(null);
    };


    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                    Ausgewählt: <b>{originalStoreData.data?.name}</b>
                </span>
            </div>
            <div className="flex items-end gap-6">
                <div className="space-y-2">
                    <Label htmlFor="start-date">Datum *</Label>
                    <Input
                        id="start-date"
                        type="date"
                        className="w-[140px]"
                        value={formatDate(startTimeObj)}
                        onChange={(e) => {
                            setStartTimeObj(parseDate(e.target.value, startTimeObj, 'Europe/Berlin'));
                            setEndTimeObj(parseDate(e.target.value, endTimeObj, 'Europe/Berlin'));
                        }}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="start-time">Startzeit *</Label>
                    <Input
                        id="start-time"
                        type="time"
                        className="w-[100px]"
                        value={formatTime(startTimeObj)}
                        onChange={(e) => setStartTimeObj(parseTime(e.target.value, startTimeObj, 'Europe/Berlin'))}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="end-time">Endzeit *</Label>
                    <Input
                        id="end-time"
                        type="time"
                        className="w-[100px]"
                        value={formatTime(endTimeObj)}
                        onChange={(e) => setEndTimeObj(parseTime(e.target.value, endTimeObj, 'Europe/Berlin'))}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
            </div>

            <div className="flex items-end gap-2">
                <div className="space-y-2">
                    <Label htmlFor="type">Typ</Label>
                    <Select
                        value={type}
                        onValueChange={(value) => setType(value as StoreScheduleEntryType | EquipmentScheduleEntryType)}
                        disabled={!canManageShiftEntries || mutationLoading || !!overwriteEntryType || isEditing}
                    >
                        <SelectTrigger id="type" className={type === StoreScheduleEntryType.AVAILABLE ? 'border-green-400' : 'border-red-400'}>
                            <SelectValue placeholder="Typ wählen" />
                        </SelectTrigger>
                        <SelectContent>
                            {!!overwriteEntryType ? (
                                <SelectItem value={overwriteEntryType}>{overwriteEntryType === StoreScheduleEntryType.AVAILABLE ? 'Verfügbarkeit' : 'Blocker'}</SelectItem>
                            ) : (
                                <>
                                    <SelectItem value={StoreScheduleEntryType.AVAILABLE}>Verfügbarkeit</SelectItem>
                                    <SelectItem value={StoreScheduleEntryType.BLOCKED}>Blocker</SelectItem>
                                </>
                            )}
                        </SelectContent>
                    </Select>
                </div>
                {type === StoreScheduleEntryType.BLOCKED && (
                    <div className="space-y-2">
                        <Label htmlFor="blockedType">Blocker Grund</Label>
                        <Select
                            value={blockedType || undefined}
                            onValueChange={(value) => {
                                if (!!value) {
                                    setBlockedType(value as StoreScheduleEntryBlockedType)
                                }
                            }}
                            disabled={!canManageShiftEntries || mutationLoading}
                        >
                            <SelectTrigger id="blockedType">
                                <SelectValue placeholder="Blocker Grund wählen" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={StoreScheduleEntryBlockedType.BREAK}>Pause</SelectItem>
                                <SelectItem value={StoreScheduleEntryBlockedType.VACATION}>Urlaub</SelectItem>
                                <SelectItem value={StoreScheduleEntryBlockedType.MEETING}>Meeting</SelectItem>
                                <SelectItem value={StoreScheduleEntryBlockedType.SICK_LEAVE}>Krankheit</SelectItem>
                                <SelectItem value={StoreScheduleEntryBlockedType.EDUCATION}>Fortbildung</SelectItem>
                                <SelectItem value={StoreScheduleEntryBlockedType.OTHER}>Sonstiges</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="description">Beschreibung (optional)</Label>
                <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="z.B. Pause, Teammeeting"
                    disabled={!canManageShiftEntries || mutationLoading}
                />
            </div>

            <div className="flex items-center gap-2">
                <Switch
                    id="is-recurring"
                    checked={isRecurring}
                    onCheckedChange={(checked) => setIsRecurring(Boolean(checked))}
                    disabled={!canManageShiftEntries || mutationLoading || isEditing}
                />
                <Label htmlFor="is-recurring" className="cursor-pointer">Wiederkehrend</Label>
            </div>

            {isRecurring && (
                <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Frequenz</Label>
                            <Select
                                value={frequency}
                                onValueChange={(value: 'DAILY' | 'WEEKLY' | 'MONTHLY') => setFrequency(value)}
                                disabled={!canManageShiftEntries || mutationLoading || isEditing}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='DAILY'>Täglich</SelectItem>
                                    <SelectItem value='WEEKLY'>Wöchentlich</SelectItem>
                                    <SelectItem value='MONTHLY'>Monatlich</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Intervall (alle x Tage/Wochen/Monate)</Label>
                            <Input
                                type="number"
                                min={1}
                                value={interval}
                                onChange={(e) => setInterval(parseInt(e.target.value, 10) || 1)}
                                disabled={!canManageShiftEntries || mutationLoading}
                            />
                        </div>
                    </div>

                    {frequency === 'WEEKLY' && (
                        <div className="space-y-2">
                            <Label>Wochentage</Label>
                            <div className="flex gap-2 flex-wrap">
                                {Object.entries(weekdays).map(([day, isSelected]) => (
                                    <label key={day} className="flex items-center gap-1 text-sm">
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked) =>
                                                setWeekdays(prev => ({ ...prev, [day]: Boolean(checked) }))
                                            }
                                            disabled={!canManageShiftEntries || mutationLoading}
                                        />
                                        {{ MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So' }[day]}
                                    </label>
                                ))}
                            </div>
                            {!Object.values(weekdays).some(selected => selected) && (
                                <p className="text-xs text-destructive">Bitte mindestens einen Tag auswählen.</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Enddatum</Label>
                        <Input
                            type="date"
                            value={recurrenceEndDateObj?.format('YYYY-MM-DD') ?? ''}
                            onChange={(e) => setRecurrenceEndDateObj(e.target.value ? dayjs(e.target.value).tz(userTimezone, true) : null)}
                            disabled={!canManageShiftEntries || mutationLoading}
                        />
                    </div>
                </div>
            )}

            <div className='flex justify-between mt-16'>
                {canManageShiftEntries && scheduleEntryId ? (
                    <Button type="button" variant="destructive" onClick={handleDelete} disabled={mutationLoading || deleteMutationLoading}>
                        Löschen {deleteMutationLoading && <Loader2 className='animate-spin' />}
                    </Button>
                ) : <div />}
                <div className="flex justify-end space-x-3">
                    <Button type="button" variant="outline" onClick={onCancel} disabled={mutationLoading || deleteMutationLoading}>
                        Abbrechen
                    </Button>
                    {canManageShiftEntries && (
                        <Button type="submit" disabled={mutationLoading || deleteMutationLoading || !selectedDataObject || (type === StoreScheduleEntryType.BLOCKED && !blockedType)}>
                            {mutationLoading ? 'Speichern...' : (isEditing ? 'Speichern' : 'Add Entry')}
                            {mutationLoading && <Loader2 className='animate-spin' />}
                        </Button>
                    )}
                </div>
            </div>
            <RecurrenceEditModeDialog
                open={recurrenceEditDialogOpen}
                isDelete={recurrenceEditDialogIsDelete}
                onSelect={handleRecurrenceEditModeSelect}
                onCancel={() => {
                    setRecurrenceEditDialogOpen(false);
                    setPendingFormData(null);
                }}
                occurrenceLabel={startTimeObj?.format('EEEE, dd.MM.yyyy')}
                occurrenceDate={startTimeObj?.format('dd.MM.yyyy')}
                seriesStartDate={originalSeriesStart?.format('dd.MM.yyyy')}
                seriesEndDate={recurrenceEndDateObj?.format('DD.MM.YYYY')}
            />
        </form>
    );
};